import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';

const RecentTrades = ({ selectedPair }) => {
  const [trades, setTrades] = useState([]);
  const [filter, setFilter] = useState('all');

  const filterOptions = [
    { value: 'all', label: 'All', icon: 'List' },
    { value: 'buy', label: 'Buys', icon: 'TrendingUp' },
    { value: 'sell', label: 'Sells', icon: 'TrendingDown' }
  ];

  // Generate mock recent trades
  useEffect(() => {
    const generateTrades = () => {
      const newTrades = [];
      const basePrice = 43250.75;

      for (let i = 0; i < 50; i++) {
        const side = Math.random() > 0.5 ? 'buy' : 'sell';
        const price = basePrice + (Math.random() - 0.5) * 1000;
        const size = Math.random() * 5;
        const timestamp = new Date(Date.now() - i * 30000); // 30 seconds apart

        newTrades.push({
          id: i,
          side,
          price,
          size,
          total: price * size,
          timestamp
        });
      }

      return newTrades;
    };

    setTrades(generateTrades());

    // Add new trades periodically
    const interval = setInterval(() => {
      setTrades(prev => {
        const newTrade = {
          id: Date.now(),
          side: Math.random() > 0.5 ? 'buy' : 'sell',
          price: 43250.75 + (Math.random() - 0.5) * 1000,
          size: Math.random() * 5,
          timestamp: new Date()
        };
        newTrade.total = newTrade.price * newTrade.size;

        return [newTrade, ...prev.slice(0, 49)]; // Keep only 50 trades
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [selectedPair]);

  const filteredTrades = trades.filter(trade => {
    if (filter === 'all') return true;
    return trade.side === filter;
  });

  const formatPrice = (price) => {
    return price.toFixed(2);
  };

  const formatSize = (size) => {
    return size.toFixed(4);
  };

  const formatTime = (timestamp) => {
    return timestamp.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const TradeRow = ({ trade, isNew = false }) => (
    <div className={`grid grid-cols-3 gap-2 px-3 py-2 text-xs font-data transition-all duration-300 ${
      isNew ? 'bg-primary-50 animate-pulse' : 'hover:bg-secondary-50'
    }`}>
      <span className={`text-right ${trade.side === 'buy' ? 'text-success' : 'text-error'}`}>
        {formatPrice(trade.price)}
      </span>
      <span className="text-right text-text-primary">
        {formatSize(trade.size)}
      </span>
      <span className="text-right text-text-secondary text-xs">
        {formatTime(trade.timestamp)}
      </span>
    </div>
  );

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-heading-semibold text-text-primary">
            Recent Trades
          </h3>
          <div className="flex items-center space-x-1">
            <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
            <span className="text-xs text-text-secondary">Live</span>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex bg-secondary-100 rounded-trading p-1">
          {filterOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => setFilter(option.value)}
              className={`flex items-center space-x-1 flex-1 py-1 px-2 rounded-trading font-body-medium text-xs transition-all duration-trading-fast ${
                filter === option.value
                  ? 'bg-surface text-text-primary shadow-trading-card'
                  : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              <Icon name={option.icon} size={12} />
              <span>{option.label}</span>
            </button>
          ))}
        </div>

        {/* Column Headers */}
        <div className="grid grid-cols-3 gap-2 px-3 py-2 mt-3 text-xs font-body-medium text-text-secondary border-b border-border">
          <span className="text-right">Price ({selectedPair ? selectedPair.split('/')[1] : ''})</span>
          <span className="text-right">Size ({selectedPair ? selectedPair.split('/')[0] : ''})</span>
          <span className="text-right">Time</span>
        </div>
      </div>

      {/* Trades List */}
      <div className="flex-1 overflow-y-auto">
        <div className="space-y-0">
          {filteredTrades.map((trade, index) => (
            <TradeRow 
              key={trade.id}
              trade={trade}
              isNew={index === 0 && Date.now() - trade.timestamp.getTime() < 5000}
            />
          ))}
        </div>
      </div>

      {/* Footer Stats */}
      <div className="p-4 border-t border-border bg-secondary-50">
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-xs text-text-secondary">24h Volume:</span>
            <span className="font-data text-sm text-text-primary">
              {trades.reduce((sum, trade) => sum + trade.size, 0).toFixed(2)} {selectedPair ? selectedPair.split('/')[0] : ''}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-xs text-text-secondary">24h Trades:</span>
            <span className="font-data text-sm text-text-primary">
              {trades.length.toLocaleString()}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-xs text-text-secondary">Avg Size:</span>
            <span className="font-data text-sm text-text-primary">
              {trades.length > 0 
                ? (trades.reduce((sum, trade) => sum + trade.size, 0) / trades.length).toFixed(4)
                : '0.0000'
              }
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecentTrades;